import React from "react";
import Big from "big.js";

import IMembershipStatus, {
  EMembershipStatusRender,
} from "./IMembershipStatus";

import { EMembership } from "@enums/EMembership";
import { classNames, MyLink } from "@features/utils/reactFunctions";

import member from "@images/member.svg?url";
import premium from "@images/premium.svg?url";
import premiumplus from "@images/premiumplus.svg?url";
import tick from "@appStyles/../images/tick.svg?url";

import * as style from "./MembershipStatusStyle.module.scss";
import * as generalStyles from "@appStyles/general.module.scss";

export const MembershipStatus: React.FunctionComponent<IMembershipStatus> = (
  props
) => {
  const membershipData = {
    [EMembership.basic]: {
      label: "BF Member",
      img: member,
    },
    [EMembership.premium]: {
      label: "BF Premium Member",
      img: premium,
    },
    [EMembership.premium_plus]: {
      label: "BF Premium+ Member",
      img: premiumplus,
    },
  };

  const calculateMembership = (amount: Big): EMembership => {
    const premiumLimit = props.membershipTresholds[EMembership.premium];
    const premiumPlusLimit =
      props.membershipTresholds[EMembership.premium_plus];

    if (amount.gte(premiumLimit) && amount.lt(premiumPlusLimit))
      return EMembership.premium;
    if (amount.gte(premiumPlusLimit)) return EMembership.premium_plus;

    return EMembership.basic;
  };

  const statusObtained = (membershipStatus: EMembership): boolean => {
    if (
      props.currentStatus === EMembership.basic &&
      [EMembership.premium, EMembership.premium_plus].includes(membershipStatus)
    ) {
      return false;
    }

    if (
      props.currentStatus === EMembership.premium &&
      EMembership.premium_plus === membershipStatus
    ) {
      return false;
    }

    return true;
  };

  const renderStatusObtainedTick = (
    membershipStatus: EMembership
  ): JSX.Element | null => {
    if (!statusObtained(membershipStatus)) return null;

    return (
      <img
        width="15"
        alt={membershipData[membershipStatus].label}
        src={tick}
        data-test="status-obtained"
      />
    );
  };

  const renderMessage = (): JSX.Element => {
    const statusChanged =
      props.currentStatus !== calculateMembership(props.amount) &&
      calculateMembership(props.amount) !== EMembership.basic;

    let message = (
      <p>
        This purchase won't change your{" "}
        <span className={style.statusUpgrade}>Membership Status.</span>
      </p>
    );

    if (statusChanged) {
      message = (
        <p>
          This purchase gives you:
          <span className={style.statusUpgrade}>
            &nbsp;{membershipData[calculateMembership(props.amount)].label}ship.
          </span>
        </p>
      );
    }

    return (
      <>
        <hr className={generalStyles.separator} />
        {message}
        <hr className={generalStyles.separator} />
      </>
    );
  };

  const isActive = (status: EMembership): boolean => {
    if (statusObtained(status)) return false;
    return (
      calculateMembership(props.amount.plus(props.walletBalance)) === status
    );
  };

  const updateInputAmount = (status: EMembership) =>
    props.onMembershipSelect(
      Big(props.membershipTresholds[status] || 0)
        .minus(props.walletBalance)
        .toString()
    );

  const renderList = (): JSX.Element => (
    <>
      {
        <ul className={classNames(style.membershipStatus, generalStyles.mT2)}>
          <hr className={generalStyles.separator} />
          <p>
            Enter the amount or choose the membership tier you would like to
            upgrade to.
          </p>
          {Object.keys(EMembership).map((membershipStatus: EMembership) => (
            <li
              className={classNames(
                style.membershipStatusOption,
                isActive(membershipStatus) && style.membershipStatusActive,
                statusObtained(membershipStatus) &&
                  style.membershipStatusNotClickable
              )}
              key={membershipData[membershipStatus].label}
              onClick={(e) => updateInputAmount(membershipStatus)}
            >
              <img
                width="32"
                alt={membershipData[membershipStatus].label}
                src={membershipData[membershipStatus].img}
              />
              <p className={style.membershipStatusLabel}>
                {membershipData[membershipStatus].label}
              </p>
              {renderStatusObtainedTick(membershipStatus)}
            </li>
          ))}
          <p className={generalStyles.mT2}>
            Learn more about membership tiers
            <MyLink
              to={`${location.pathname.split("/funds")[0]}/membership`}
              className={style.statusUpgrade}
              target="_blank"
            >
              &nbsp;here.
            </MyLink>
          </p>
          <hr className={generalStyles.separator} />
        </ul>
      }
    </>
  );

  const renderInline = (): JSX.Element => renderMessage();

  if (props.renderType === EMembershipStatusRender.list) {
    return renderList();
  }

  return renderInline();
};

export default React.memo(MembershipStatus);
